import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Users, Award, Rocket, ArrowRight, AlertCircle, Clock, Timer } from 'lucide-react';
import { BrutalButton } from '@/components/ui/BrutalButton';
import { BrutalCard } from '@/components/ui/BrutalCard';
import { BrutalRadioGroup, BrutalRadioGroupItem } from '@/components/ui/BrutalRadioGroup';
import { BrutalProgress } from '@/components/ui/BrutalProgress';
import { BrutalBadge } from '@/components/ui/BrutalBadge';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { 
  screeningQuestions, 
  THRESHOLDS, 
  TIME_LIMIT_SECONDS,
  SUSPICIOUS_ANSWER_TIME,
  TOTAL_QUESTIONS,
  shuffleArray,
  getBadgeTier,
  getRetryDate,
  formatTime,
  getStatusFromScore,
  type ScreeningStatus
} from '@/data/screeningQuestions';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';

const BecomeProfessional = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, isAuthenticated, refreshScreeningStatus } = useAuth();
  
  // Core state
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [existingApplication, setExistingApplication] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasStartedQuestionnaire, setHasStartedQuestionnaire] = useState(false);
  
  // New tracking state
  const [timeRemaining, setTimeRemaining] = useState(TIME_LIMIT_SECONDS);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());
  const [timePerQuestion, setTimePerQuestion] = useState<Record<string, number>>({});
  const [shuffledOptions, setShuffledOptions] = useState<Record<string, { value: string; label: string; points: number }[]>>({});
  const [attemptNumber, setAttemptNumber] = useState(1);
  const [canRetry, setCanRetry] = useState(true);
  const [retryDate, setRetryDate] = useState<Date | null>(null);

  const currentLang = i18n.language as 'it' | 'en' | 'es';
  const questions = screeningQuestions[currentLang] || screeningQuestions.it;
  const currentQuestion = questions[currentStep];
  const progress = ((currentStep + 1) / questions.length) * 100;

  // Shuffle options when questionnaire starts
  useEffect(() => {
    if (hasStartedQuestionnaire && Object.keys(shuffledOptions).length === 0) {
      const shuffled: Record<string, { value: string; label: string; points: number }[]> = {};
      questions.forEach(q => {
        shuffled[q.id] = shuffleArray([...q.options]);
      });
      setShuffledOptions(shuffled);
      setStartTime(Date.now());
      setQuestionStartTime(Date.now());
    }
  }, [hasStartedQuestionnaire, questions]);

  // Timer countdown
  useEffect(() => {
    if (!hasStartedQuestionnaire || showResult || isSubmitting) return;
    
    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          handleTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [hasStartedQuestionnaire, showResult, isSubmitting]);

  // Handle timeout - auto submit with current answers
  const handleTimeout = useCallback(() => {
    toast({
      title: t('screening.timeExpired'),
      description: t('screening.timeExpiredDesc'),
      variant: 'error',
    });
    handleSubmit(true);
  }, [toast, t]);

  // Check for existing application on mount
  useEffect(() => {
    const checkExistingApplication = async () => {
      if (isAuthenticated && user) {
        try {
          const { data } = await supabase
            .from('screening_applications')
            .select('id, user_id, status, score, submitted_at, rejection_reason, retry_available_at, attempt_number, profile_id, created_at, updated_at')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          
          if (data) {
            // Check if approved
            if (data.status === 'approved') {
              navigate('/dashboard');
              return;
            }
            
            // Block rejected users
            if (data.status === 'rejected') {
              setExistingApplication(data);
              setIsLoading(false);
              return;
            }
            
            // Check retry availability
            if (data.retry_available_at) {
              const retryAvailableAt = new Date(data.retry_available_at);
              if (retryAvailableAt > new Date()) {
                setCanRetry(false);
                setRetryDate(retryAvailableAt);
              } else {
                setAttemptNumber((data.attempt_number || 1) + 1);
              }
            }
            
            setExistingApplication(data);
          }
        } catch (error) {
          console.error('Error checking application:', error);
        }
      }
      setIsLoading(false);
    };

    checkExistingApplication();
  }, [isAuthenticated, user, navigate]);

  const handleAnswer = (value: string) => {
    // Record time spent on this question
    const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    setTimePerQuestion(prev => ({
      ...prev,
      [currentQuestion.id]: timeSpent
    }));
    
    setAnswers({ ...answers, [currentQuestion.id]: value });
  };

  const canGoNext = answers[currentQuestion.id] !== undefined;

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
      setQuestionStartTime(Date.now());
    } else {
      handleSubmit(false);
    }
  };

  const calculateScore = useCallback(() => {
    let score = 0;
    questions.forEach((question) => {
      // Skip questions excluded from score calculation (e.g., q15 feedback question)
      if (question.excludeFromScore) return;
      
      const answer = answers[question.id];
      if (answer) {
        const option = question.options.find((opt) => opt.value === answer);
        if (option) {
          score += option.points;
        }
      }
    });
    return score;
  }, [questions, answers]);

  const handleStartQuestionnaire = () => {
    if (!isAuthenticated) {
      sessionStorage.setItem('returnTo', '/become-professional');
      navigate('/auth/register');
      return;
    }
    
    if (!canRetry && retryDate) {
      toast({
        title: t('screening.retryNotAvailable'),
        description: t('screening.retryAvailableIn', { 
          date: retryDate.toLocaleDateString(currentLang) 
        }),
        variant: 'error',
      });
      return;
    }
    
    setHasStartedQuestionnaire(true);
  };

  const handleSubmit = async (isTimeout: boolean = false) => {
    if (!isAuthenticated || !user) {
      toast({
        title: t('auth.loginRequired'),
        description: t('auth.pleaseLogin'),
        variant: 'error',
      });
      navigate('/auth/register');
      return;
    }

    setIsSubmitting(true);
    const score = calculateScore();
    const status: ScreeningStatus = getStatusFromScore(score);
    const isApproved = status === 'approved';
    const badgeTier = getBadgeTier(score);
    const totalTimeSeconds = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;
    
    // Count suspicious answers (< 5 seconds)
    const suspiciousAnswers = Object.values(timePerQuestion)
      .filter(time => time < SUSPICIOUS_ANSWER_TIME).length;
    
    // Calculate retry date if not approved
    const retryAvailableAt = isApproved ? null : getRetryDate(score);
    
    // Extract training preference (q18) separately
    const trainingPreference = answers['q18'] || null;

    try {
      // Check if application already exists (approved)
      const { data: existingApp } = await supabase
        .from('screening_applications')
        .select('id, status')
        .eq('user_id', user.id)
        .eq('status', 'approved')
        .maybeSingle();

      if (existingApp) {
        toast({
          title: t('screening.alreadyApproved'),
          description: t('screening.alreadyApprovedDesc'),
        });
        navigate('/dashboard');
        return;
      }

      // Create or update profile if approved
      let profileId = null;
      if (isApproved) {
        // Check if profile already exists
        const { data: existingProfile } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (existingProfile) {
          // Use existing profile and update verification status
          profileId = existingProfile.id;
          await supabase
            .from('profiles')
            .update({
              is_verified: true,
              verified_at: new Date().toISOString(),
            })
            .eq('id', existingProfile.id);
        } else {
          // Create new profile only if it doesn't exist
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .insert({
              user_id: user.id,
              business_name: 'Nuovo Professionista',
              slug: `professional-${Date.now()}`,
              is_verified: true,
              verified_at: new Date().toISOString(),
            })
            .select()
            .single();

          if (profileError) throw profileError;
          profileId = profile.id;
        }
      }

      // Create application with tracking data
      const { error } = await supabase.from('screening_applications').insert({
        user_id: user.id,
        user_email: user.email,
        answers,
        score,
        status: status, // 3 stati: approved, pending_review, rejected (solo admin)
        profile_id: profileId,
        reviewed_at: isApproved ? new Date().toISOString() : null,
        time_per_question: timePerQuestion,
        total_time_seconds: totalTimeSeconds,
        ip_address: null, // Will be captured by edge function if needed
        user_agent: navigator.userAgent,
        badge_tier: badgeTier,
        suspicious_answers: suspiciousAnswers,
        retry_available_at: retryAvailableAt?.toISOString() || null,
        attempt_number: attemptNumber,
        training_preference: trainingPreference, // Save q18 response separately
      });

      if (error) throw error;

      setFinalScore(score);
      setShowResult(true);

      // Redirect to pricing after 3 seconds if approved
      if (isApproved) {
        // Refresh screening status in AuthContext before redirect
        await refreshScreeningStatus();
        
        setTimeout(() => {
          navigate('/pricing');
        }, 3000);
      }

    } catch (error: any) {
      console.error('Screening submission error:', error);
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get current question options (shuffled)
  const currentOptions = useMemo(() => {
    if (!currentQuestion) return [];
    return shuffledOptions[currentQuestion.id] || currentQuestion.options;
  }, [currentQuestion, shuffledOptions]);

  // Timer color based on remaining time
  const getTimerColor = () => {
    if (timeRemaining < 60) return 'text-destructive';
    if (timeRemaining < 300) return 'text-warning';
    return 'text-foreground';
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-muted-foreground">{t('common.loading')}</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Cannot retry yet
  if (!canRetry && retryDate && !hasStartedQuestionnaire) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-16">
          <BrutalCard className="max-w-2xl mx-auto text-center space-y-6">
            <Clock className="w-20 h-20 text-warning mx-auto" />
            <h1 className="font-heading font-bold text-4xl text-dark">{t('screening.retryNotAvailable')}</h1>
            <p className="font-body text-xl text-muted-foreground">
              {t('screening.retryAvailableIn', { date: retryDate.toLocaleDateString(currentLang) })}
            </p>
            <div className="flex gap-4 justify-center">
              <BrutalButton variant="outline" onClick={() => navigate('/')}>
                {t('common.home')}
              </BrutalButton>
              <BrutalButton onClick={() => navigate('/dashboard')}>
                {t('common.dashboard')}
              </BrutalButton>
            </div>
          </BrutalCard>
        </main>
        <Footer />
      </div>
    );
  }

  // Show existing rejected application
  if (existingApplication && existingApplication.status === 'rejected') {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-16">
          <BrutalCard className="max-w-2xl mx-auto text-center space-y-6">
            <AlertCircle className="w-20 h-20 text-destructive mx-auto" />
            <h1 className="font-heading font-bold text-4xl text-dark">{t('screening.status.rejected')}</h1>
            <p className="font-body text-xl text-muted-foreground">
              {t('screening.status.rejectedDesc')}
            </p>
            {existingApplication.rejection_reason && (
              <div className="bg-surface p-4 border-[3px] border-dark text-left">
                <p className="font-heading font-bold text-sm mb-1">{t('screening.rejectionReason')}:</p>
                <p className="font-body text-muted-foreground">{existingApplication.rejection_reason}</p>
              </div>
            )}
            <div className="flex gap-4 justify-center">
              <BrutalButton variant="outline" onClick={() => navigate('/')}>
                {t('common.home')}
              </BrutalButton>
              <BrutalButton onClick={() => navigate('/dashboard')}>
                {t('common.dashboard')}
              </BrutalButton>
            </div>
          </BrutalCard>
        </main>
        <Footer />
      </div>
    );
  }

  // Show existing pending application (includes legacy 'pending' and 'on_hold')
  if (existingApplication && ['pending_review', 'pending', 'on_hold'].includes(existingApplication.status)) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-16">
          <BrutalCard className="max-w-2xl mx-auto text-center space-y-6">
            <Clock className="w-20 h-20 text-warning mx-auto" />
            <h1 className="font-heading font-bold text-4xl text-dark">{t('screening.status.pending')}</h1>
            <p className="font-body text-xl text-muted-foreground">
              {t('screening.status.pendingDesc')}
            </p>
            <div className="flex gap-4 justify-center">
              <BrutalButton variant="outline" onClick={() => navigate('/')}>
                {t('common.home')}
              </BrutalButton>
              <BrutalButton onClick={() => navigate('/dashboard')}>
                {t('common.dashboard')}
              </BrutalButton>
            </div>
          </BrutalCard>
        </main>
        <Footer />
      </div>
    );
  }

  // Show result - neutral message
  if (showResult) {
    const isApproved = finalScore >= THRESHOLDS.APPROVED;
    
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-16">
          <BrutalCard className="max-w-2xl mx-auto text-center space-y-6">
            {isApproved ? (
              <>
                <CheckCircle className="w-20 h-20 text-success mx-auto" />
                <h1 className="font-heading font-bold text-4xl text-dark">
                  {t('screening.result.approved')}
                </h1>
                <p className="font-body text-xl text-muted-foreground">
                  {t('screening.result.approvedDesc')}
                </p>
                <BrutalButton size="lg" onClick={() => navigate('/pricing')}>
                  {t('screening.result.choosePlan')}
                </BrutalButton>
              </>
            ) : (
              <>
                <AlertCircle className="w-20 h-20 text-muted-foreground mx-auto" />
                <h1 className="font-heading font-bold text-3xl text-dark">
                  {t('screening.completedNeutral')}
                </h1>
                <p className="font-body text-muted-foreground">
                  {t('screening.completedNeutralDesc')}
                </p>
                <div className="flex gap-4 justify-center">
                  <BrutalButton variant="outline" onClick={() => navigate('/')}>
                    {t('common.home')}
                  </BrutalButton>
                  <BrutalButton onClick={() => navigate('/dashboard')}>
                    {t('common.dashboard')}
                  </BrutalButton>
                </div>
              </>
            )}
          </BrutalCard>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-16">
        {/* Hero - Only show before starting */}
        {!hasStartedQuestionnaire && (
          <>
            <div className="max-w-4xl mx-auto text-center space-y-6 mb-16">
              {!isAuthenticated && (
                <BrutalBadge variant="secondary" className="mb-4">
                  {t('screening.loginRequired')}
                </BrutalBadge>
              )}
              <h1 className="font-heading font-bold text-4xl md:text-5xl text-dark mb-4">{t('screening.heroTitle')}</h1>
              <p className="font-body text-xl text-muted-foreground">{t('screening.heroSubtitle')}</p>
              {!isAuthenticated && (
                <p className="font-body text-muted-foreground">
                  {t('screening.registerPrompt')}
                </p>
              )}
            </div>

            {/* Benefits */}
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-16">
              <BrutalCard className="text-center space-y-4">
                <Users className="w-12 h-12 mx-auto text-primary" />
                <h3 className="font-heading font-bold text-xl text-dark">{t('screening.benefit1Title')}</h3>
                <p className="font-body text-muted-foreground">{t('screening.benefit1Desc')}</p>
              </BrutalCard>
              <BrutalCard className="text-center space-y-4">
                <Award className="w-12 h-12 mx-auto text-primary" />
                <h3 className="font-heading font-bold text-xl text-dark">{t('screening.benefit2Title')}</h3>
                <p className="font-body text-muted-foreground">{t('screening.benefit2Desc')}</p>
              </BrutalCard>
              <BrutalCard className="text-center space-y-4">
                <Rocket className="w-12 h-12 mx-auto text-primary" />
                <h3 className="font-heading font-bold text-xl text-dark">{t('screening.benefit3Title')}</h3>
                <p className="font-body text-muted-foreground">{t('screening.benefit3Desc')}</p>
              </BrutalCard>
            </div>

            {/* Process Steps */}
            <div className="max-w-3xl mx-auto mb-16">
              <h2 className="font-heading font-bold text-2xl text-center mb-8 text-dark">{t('screening.howItWorks')}</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {[1, 2, 3].map((step) => (
                  <div key={step} className="text-center space-y-2">
                    <div className="w-12 h-12 bg-primary border-[3px] border-dark shadow-[4px_4px_0px_0px] shadow-dark text-dark flex items-center justify-center text-xl font-heading font-bold mx-auto">
                      {step}
                    </div>
                    <h4 className="font-heading font-semibold text-dark">{t(`screening.step${step}Title`)}</h4>
                    <p className="font-body text-sm text-muted-foreground">{t(`screening.step${step}Desc`)}</p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Questionnaire */}
        <div className="max-w-2xl mx-auto">
          {!hasStartedQuestionnaire ? (
            <BrutalCard className="space-y-6 text-center">
              <h2 className="font-heading font-bold text-2xl text-dark">{t('screening.readyToStart')}</h2>
              <p className="font-body text-muted-foreground">
                {isAuthenticated ? t('screening.startInfo') : t('screening.registerToStart')}
              </p>
              
              {/* Info about questionnaire */}
              <div className="bg-surface p-4 border-[3px] border-dark text-left space-y-2">
                <div className="flex items-center gap-2 text-sm font-body">
                  <Timer className="w-4 h-4" />
                  <span>{t('screening.timeLimit', { minutes: Math.floor(TIME_LIMIT_SECONDS / 60) })}</span>
                </div>
                <div className="flex items-center gap-2 text-sm font-body">
                  <CheckCircle className="w-4 h-4" />
                  <span>{t('screening.questionsCount', { count: questions.length })}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  {t('screening.noGoingBack')}
                </p>
              </div>

              <BrutalButton size="lg" onClick={handleStartQuestionnaire}>
                {isAuthenticated ? t('screening.startQuestionnaire') : t('screening.registerAndStart')}
              </BrutalButton>
            </BrutalCard>
          ) : (
            <BrutalCard className="space-y-6">
              {/* Timer */}
              <div className={`flex items-center justify-center gap-2 text-2xl font-heading font-bold ${getTimerColor()}`}>
                <Timer className="w-6 h-6" />
                <span>{formatTime(timeRemaining)}</span>
              </div>

              {/* Progress */}
              <div className="space-y-2">
                <div className="flex justify-between font-body text-sm text-muted-foreground mb-2">
                  <span>
                    {t('screening.question')} {currentStep + 1} {t('common.of')} {questions.length}
                  </span>
                  <span className="font-heading font-bold">{Math.round(progress)}%</span>
                </div>
                <BrutalProgress value={progress} />
              </div>

              {/* Question */}
              <div className="space-y-4">
                <h3 className="font-heading font-bold text-xl text-dark">{currentQuestion.text}</h3>
                <BrutalRadioGroup key={currentQuestion.id} value={answers[currentQuestion.id] || ''} onValueChange={handleAnswer}>
                  <div className="space-y-3">
                    {currentOptions.map((option) => (
                      <div key={option.value} className="flex items-center space-x-3 p-4 border-[3px] border-dark hover:bg-surface transition-colors cursor-pointer">
                        <BrutalRadioGroupItem value={option.value} id={`${currentQuestion.id}-${option.value}`} />
                        <Label htmlFor={`${currentQuestion.id}-${option.value}`} className="flex-1 cursor-pointer font-body">
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </BrutalRadioGroup>
              </div>

              {/* Navigation - Only forward */}
              <div className="flex justify-end pt-4">
                <BrutalButton onClick={handleNext} disabled={!canGoNext || isSubmitting}>
                  {isSubmitting ? t('common.submitting') : 
                   currentStep === questions.length - 1 ? t('common.submit') : t('common.next')}
                  {currentStep < questions.length - 1 && <ArrowRight className="w-4 h-4 ml-2" />}
                </BrutalButton>
              </div>
            </BrutalCard>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BecomeProfessional;
